/**
 * 微信 JSSDK 配置 + 小程序开放标签跳转工具（独立实现，专供 AI 对话页"去丽天车队预约"纠偏引导使用）。
 * 不影响首页 panelTpl.vue 的蒸洗入口逻辑。
 *
 * 用法：
 *   import { ensureWxConfig, mountLaunchButton } from '@/utils/wxLaunchWeapp';
 *   ensureWxConfig().then(() => mountLaunchButton('容器id', { path: 'pages/index/index', text: '去丽天车队预约' }));
 *
 * 约束：
 *   - 只能在微信内置浏览器中使用（依赖 window.wx 与 wx-open-launch-weapp 开放标签）
 *   - 开放标签必须由用户点击触发，按钮通过 wxtag-template 渲染
 */
import api from '@/fetch/api';

let configPromise = null;

/**
 * 初始化微信 JSSDK 配置（仅需成功一次，全局复用）。
 * 签名 URL 规则与首页一致：iOS 用首次进入页 URL（sessionStorage wx_entry_url），安卓用当前页 URL（去参）。
 * @returns {Promise<void>} resolve 表示 wx.config 成功（wx.ready）
 */
export function ensureWxConfig() {
  if (configPromise) {
    return configPromise;
  }
  configPromise = new Promise((resolve, reject) => {
    if (!window.wx) {
      reject(new Error('微信环境异常，请使用微信打开'));
      return;
    }
    const isWechat = /MicroMessenger/i.test(navigator.userAgent);
    if (!isWechat) {
      reject(new Error('请在微信中打开此页面'));
      return;
    }

    let currentUrl = '';
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isIOS) {
      const entryUrl = sessionStorage.getItem('wx_entry_url');
      if (entryUrl) {
        currentUrl = entryUrl;
      } else {
        currentUrl = window.location.href.split('?')[0].split('#')[0];
        try {
          sessionStorage.setItem('wx_entry_url', currentUrl);
        } catch (e) {
          console.warn('[wxLaunchWeapp] sessionStorage 写入失败:', e);
        }
      }
    } else {
      currentUrl = window.location.href.split('?')[0].split('#')[0];
    }

    api.getWxJsapiSignature(currentUrl).then((res) => {
      const signatureData = typeof res === 'string' ? JSON.parse(res) : res;
      const ok = signatureData
        && (signatureData.code === 200 || signatureData.respCode === 0)
        && signatureData.data;
      if (!ok) {
        reject(new Error('获取微信签名失败，请稍后重试'));
        return;
      }
      const { appId, timestamp, nonceStr, signature } = signatureData.data;
      window.wx.config({
        debug: false,
        appId: appId,
        timestamp: timestamp,
        nonceStr: nonceStr,
        signature: signature,
        jsApiList: [],
        openTagList: ['wx-open-launch-weapp'],
      });
      window.wx.ready(() => resolve());
      window.wx.error((err) => {
        reject(new Error((err && err.errMsg) || '微信配置失败'));
      });
    }).catch((err) => {
      console.error('[wxLaunchWeapp] 获取签名失败:', err);
      reject(new Error('获取微信签名失败，请稍后重试'));
    });
  });
  return configPromise;
}

/**
 * 在指定容器内挂载 wx-open-launch-weapp 跳转按钮。
 * @param {string} containerId 容器元素 id
 * @param {object} opts path: 小程序页面路径（可带参数）；text: 按钮文案
 * @returns {boolean} 是否挂载成功
 */
export function mountLaunchButton(containerId, opts = {}) {
  const box = document.getElementById(containerId);
  if (!box) {
    console.error('[wxLaunchWeapp] 找不到容器:', containerId);
    return false;
  }
  const path = opts.path || 'pages/index/index';
  const text = opts.text || '去丽天车队预约';

  box.innerHTML = '';

  const launchBtn = document.createElement('wx-open-launch-weapp');
  launchBtn.setAttribute('username', 'gh_f03add8cf8b3');
  launchBtn.setAttribute('path', path);
  launchBtn.style.cssText = 'display:flex;align-items:center;justify-content:center;width:100%;';

  const scriptEl = document.createElement('script');
  scriptEl.setAttribute('type', 'text/wxtag-template');
  scriptEl.style.cssText = 'display:flex;align-items:center;justify-content:center;width:100%;';

  const buttonEl = document.createElement('button');
  buttonEl.style.cssText = [
    'width:100%',
    'height:44px',
    'background:#299ff1',
    'color:#fff',
    'border:none',
    'border-radius:22px',
    'font-size:16px',
    'font-weight:bold',
    'cursor:pointer',
    'display:flex',
    'align-items:center',
    'justify-content:center',
  ].join(';');
  buttonEl.textContent = text;

  scriptEl.appendChild(buttonEl);
  launchBtn.appendChild(scriptEl);
  box.appendChild(launchBtn);
  return true;
}
